import { useState } from "react";
import { toast } from "sonner";
import { Plus, X, Tags } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";

const DEFAULT_INCOME = [
  "Sales Revenue", "Service Income", "Interest Income", "Refunds Received", "Other Income",
];
const DEFAULT_EXPENSE = [
  "Cost of Goods Sold", "Salaries & Wages", "Rent", "Utilities", "Office Supplies",
  "Travel", "Marketing & Advertising", "Software & Subscriptions", "Bank Fees & Charges",
  "Taxes", "Professional Services", "Insurance", "Meals & Entertainment", "Loan & EMI",
  "Other Expense",
];

const Column = ({ title, kind, items, defaults, onAdd, onDelete }) => {
  const [val, setVal] = useState("");
  const add = () => {
    const n = val.trim();
    if (!n) return;
    onAdd(n, kind);
    setVal("");
  };
  return (
    <div className="space-y-3">
      <span className="text-xs tracking-[0.2em] uppercase font-semibold text-gray-500">{title}</span>
      <div className="flex gap-2">
        <Input
          data-testid={`new-category-input-${kind}`}
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()}
          placeholder="Add category…"
          className="h-9"
        />
        <Button
          data-testid={`add-category-button-${kind}`}
          onClick={add}
          className="bg-black text-white hover:bg-gray-800 hover:text-white h-9 px-3"
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>
      <div className="flex flex-wrap gap-2">
        {items.map((c) => {
          const isDefault = defaults.includes(c);
          return (
            <span
              key={c}
              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs border ${
                isDefault
                  ? "bg-gray-50 border-gray-200 text-gray-600"
                  : "bg-black text-white border-black"
              }`}
            >
              {c}
              {!isDefault && (
                <button
                  data-testid={`delete-category-${c}`}
                  onClick={() => onDelete(c, kind)}
                  className="hover:text-rose-300"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </span>
          );
        })}
      </div>
    </div>
  );
};

export const CategoryManager = ({ categories, onChange }) => {
  const [open, setOpen] = useState(false);

  const add = async (name, kind) => {
    try {
      const res = await api.addCategory({ name, kind });
      onChange?.(res);
      toast.success(`Added "${name}"`);
    } catch {
      toast.error("Could not add category");
    }
  };
  const del = async (name, kind) => {
    try {
      const res = await api.deleteCategory(name, kind);
      onChange?.(res);
      toast.success(`Removed "${name}"`);
    } catch {
      toast.error("Could not remove category");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          data-testid="manage-categories-button"
          className="bg-white border-gray-200 text-gray-900 hover:bg-gray-50"
        >
          <Tags className="h-4 w-4 mr-1" /> Categories
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="category-manager-dialog" className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="font-heading">Manage P&L Categories</DialogTitle>
          <DialogDescription>
            Add your own income and expense categories. Defaults can't be removed; custom ones are
            shown in black.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Column
            title="Income"
            kind="income"
            items={categories.income}
            defaults={DEFAULT_INCOME}
            onAdd={add}
            onDelete={del}
          />
          <Column
            title="Expenses"
            kind="expense"
            items={categories.expense}
            defaults={DEFAULT_EXPENSE}
            onAdd={add}
            onDelete={del}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};
